import moment from "moment";
import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate as useNavigateOriginal, useParams } from "react-router-dom";

import { useAuth } from "./auth-context";

import { ENDPOINTS } from "@/api/application-api";
import { getInitialActiveWorkspaceId, persistActiveWorkspaceId } from "@/utils/activeWorkspace";
import { isIntercomHost } from "@/utils/intercom";
import {
  useIntercomVerification,
  usePromptLayerApiKeys,
  useWebhook,
  useWorkspaceMembers,
  useWorkspaces,
} from "@/queries";
import { PromptLayerApiKey } from "@/types/promptLayerApiKeys";
import { Workspace } from "@/types/workspaces";

interface UserContextType {
  promptLayerWorkspaceApiKeys: PromptLayerApiKey[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workspaceMembers: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workspaceWebhooks: any[];
  workspaces: Workspace[];
  workspacePlanName: string | undefined;
  activeWorkspace: Workspace | undefined;
  activeWorkspaceId: number | null;
  setWorkspaceId: (workspaceId: number) => void;
  isNewUser: boolean;
}

export { getInitialActiveWorkspaceId } from "@/utils/activeWorkspace";

const UserContext = createContext<UserContextType | null>(null);

function UserProvider(props: PropsWithChildren) {
  const auth = useAuth();
  const { userToken } = auth!;
  const useNavigate = () => {
    try {
      return useNavigateOriginal();
    } catch {
      return undefined;
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (!userToken) {
      return;
    }

    fetch(ENDPOINTS.get_user, {
      headers: { Authorization: `Bearer ${userToken}` },
    })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .catch(() => auth?.logout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  const intercomVerification = useIntercomVerification(userToken!);

  const [activeWorkspaceId, setActiveWorkspaceId] = useState<number | null>(
    getInitialActiveWorkspaceId(),
  );

  const promptLayerApiKeysQuery = usePromptLayerApiKeys(userToken!, activeWorkspaceId!);
  const workspacesQuery = useWorkspaces();
  const workspaceMembersQuery = useWorkspaceMembers(userToken!, activeWorkspaceId!);
  const workspaceWebhookQuery = useWebhook(userToken!, activeWorkspaceId!);
  const activeWorkspace = useMemo(
    () => workspacesQuery.data?.workspaces?.find(workspace => workspace?.id === activeWorkspaceId!),
    [workspacesQuery.data?.workspaces, activeWorkspaceId],
  );
  const params = useParams();

  const setWorkspaceId = (workspaceId: number) => {
    setActiveWorkspaceId(workspaceId);
    persistActiveWorkspaceId(workspaceId);
  };

  /**
   * Sync the active workspace from the URL when a workspace route is mounted.
   * Skip when there is no workspaceId param (e.g. /home) so we do not overwrite
   * the persisted last-active workspace with an invalid value.
   */
  useEffect(() => {
    const workspaceIdFromParams = Number(params?.workspaceId);
    if (
      !params?.workspaceId ||
      !Number.isFinite(workspaceIdFromParams) ||
      workspaceIdFromParams <= 0 ||
      activeWorkspaceId === workspaceIdFromParams ||
      location.pathname.includes("/onboarding")
    ) {
      return;
    }

    setWorkspaceId(workspaceIdFromParams);
  }, [activeWorkspaceId, params, navigate]);

  // Workspace plan details for meticolous recorder
  const workspacePlanName = activeWorkspace?.admin_billing_plan_name;

  const intercomUserHash = intercomVerification.data?.intercom_verification;
  const userId = auth?.user?.id;
  const userEmail = auth?.user?.email;

  useEffect(() => {
    if (!isIntercomHost(window.location.hostname)) {
      return;
    }
    if (!userId || !intercomUserHash) {
      return;
    }

    // @ts-expect-error Intercom is not typed
    window.Intercom?.("update", {
      user_id: userId,
      email: userEmail,
      user_hash: intercomUserHash,
    });
  }, [userId, userEmail, intercomUserHash]);

  const workspaces: Workspace[] =
    workspacesQuery.data?.workspaces?.map(workspace => ({
      ...workspace,
      is_admin: workspace.admin_user_ids?.includes(auth?.user?.id ?? 0),
    })) ?? [];

  const isNewUser = useMemo(
    () => moment().diff(moment(auth?.user?.created_at), "days") < 14,
    [auth?.user?.created_at],
  );

  return (
    <UserContext.Provider
      value={{
        promptLayerWorkspaceApiKeys: promptLayerApiKeysQuery.data?.api_keys ?? [],
        user: auth?.user,
        isNewUser,
        workspaceMembers: workspaceMembersQuery.data?.workspace_members ?? [],
        workspaceWebhooks: workspaceWebhookQuery.data?.webhooks ?? [],
        workspaces,
        workspacePlanName,
        activeWorkspace: activeWorkspace,
        setWorkspaceId,
        activeWorkspaceId: activeWorkspaceId,
      }}
      {...props}
    />
  );
}

const useUser = () => {
  const user = useContext(UserContext);
  if (!user) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return user;
};

export { UserProvider, useUser };
